import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaMapMarkerAlt, FaPhone, FaClock, 
  FaEnvelope, FaFacebookF, FaInstagram, 
  FaTwitter, FaLinkedinIn,
  FaCheckCircle, FaDirections
} from 'react-icons/fa';

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle');
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clinicLocation = {
    address: "Maxfax Dental Clinic, Alam Sher Center, Nishtar Rd, Near Multan MRI, Lalazar Colony, Multan, 66000",
    phone: "03014484633",
    email: "info@maxfaxdental.com"
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    setTimeout(() => {
      setFormStatus('success');
    }, 2000);
  };

  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clinicLocation.address)}`;
    window.open(url, '_blank');
  };

  const getDirections = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const url = `https://www.google.com/maps/dir/${position.coords.latitude},${position.coords.longitude}/${encodeURIComponent(clinicLocation.address)}`;
          window.open(url, '_blank');
        },
        () => {
          openGoogleMaps();
        }
      );
    } else {
      openGoogleMaps();
    }
  };

  const styles = {
    contact: {
      padding: isMobile ? '60px 0' : '100px 0',
      background: 'linear-gradient(135deg, #151E2F, #0A0F1C)',
      position: 'relative'
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 40px',
      position: 'relative',
      zIndex: 2
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: isMobile ? '30px' : '40px'
    },
    subtitle: {
      color: '#C8A97E',
      fontSize: isMobile ? '14px' : '16px',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '3px' : '4px',
      marginBottom: '10px',
      display: 'block',
      fontWeight: 600
    },
    h2: {
      fontSize: isMobile ? 'clamp(28px, 6vw, 36px)' : 'clamp(36px, 5vw, 48px)',
      color: '#ffffff',
      marginBottom: '15px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 800
    },
    titleGradient: {
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    divider: {
      width: isMobile ? '60px' : '80px',
      height: '3px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      margin: '0 auto',
      borderRadius: '2px'
    },
    mapSection: {
      marginBottom: isMobile ? '30px' : '40px',
      borderRadius: isMobile ? '15px' : '20px',
      overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(200, 169, 126, 0.2)',
      position: 'relative',
      height: isMobile ? '200px' : '250px',
      maxWidth: '800px',
      margin: '0 auto 30px auto'
    },
    mapContainer: {
      width: '100%',
      height: '100%',
      position: 'relative'
    },
    mapIframe: {
      width: '100%',
      height: '100%',
      border: 'none'
    },
    mapOverlay: {
      position: 'absolute',
      bottom: '10px',
      right: '10px',
      left: isMobile ? '10px' : 'auto',
      display: 'flex',
      gap: '8px',
      zIndex: 10,
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      justifyContent: isMobile ? 'center' : 'flex-end'
    },
    mapButton: {
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      color: '#0A0F1C',
      border: 'none',
      padding: isMobile ? '8px 12px' : '10px 20px',
      borderRadius: '30px',
      fontWeight: 600,
      fontSize: isMobile ? '12px' : '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 5px 15px rgba(200, 169, 126, 0.3)',
      minWidth: isMobile ? '120px' : 'auto',
      justifyContent: 'center'
    },
    content: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1.2fr',
      gap: isMobile ? '20px' : '30px',
      marginTop: '20px'
    },
    infoCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '20px' : '30px',
      border: '1px solid rgba(200, 169, 126, 0.15)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
    },
    infoTitle: {
      fontSize: isMobile ? '22px' : '26px',
      color: '#ffffff',
      marginBottom: isMobile ? '20px' : '25px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700,
      textAlign: isMobile ? 'center' : 'left'
    },
    infoItem: {
      display: 'flex',
      gap: isMobile ? '12px' : '15px',
      marginBottom: isMobile ? '15px' : '20px',
      padding: isMobile ? '12px' : '15px',
      background: 'rgba(200, 169, 126, 0.03)',
      borderRadius: '12px',
      border: '1px solid rgba(200, 169, 126, 0.1)',
      transition: 'all 0.3s',
      cursor: 'pointer',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'center' : 'flex-start',
      textAlign: isMobile ? 'center' : 'left'
    },
    infoIcon: {
      width: isMobile ? '40px' : '45px',
      height: isMobile ? '40px' : '45px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '16px' : '18px',
      color: '#0A0F1C',
      flexShrink: 0,
      boxShadow: '0 5px 15px rgba(200, 169, 126, 0.3)',
      marginBottom: isMobile ? '8px' : '0'
    },
    infoContent: {
      flex: 1
    },
    infoLabel: {
      fontSize: '12px',
      color: '#C8A97E',
      marginBottom: '3px',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      fontWeight: 600
    },
    infoValue: {
      fontSize: isMobile ? '14px' : '15px',
      color: '#ffffff',
      lineHeight: 1.5,
      fontWeight: 400
    },
    socialLinks: {
      display: 'flex',
      gap: '12px',
      marginTop: '20px',
      justifyContent: isMobile ? 'center' : 'flex-start',
      flexWrap: 'wrap'
    },
    socialLink: {
      width: isMobile ? '40px' : '45px',
      height: isMobile ? '40px' : '45px',
      background: 'rgba(200, 169, 126, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#C8A97E',
      fontSize: isMobile ? '16px' : '18px',
      transition: 'all 0.3s',
      cursor: 'pointer',
      border: '1px solid rgba(200, 169, 126, 0.2)'
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(10px)',
      borderRadius: isMobile ? '15px' : '20px',
      padding: isMobile ? '20px' : '30px',
      border: '1px solid rgba(200, 169, 126, 0.15)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
    },
    formTitle: {
      fontSize: isMobile ? '22px' : '26px',
      color: '#ffffff',
      marginBottom: isMobile ? '20px' : '25px',
      fontFamily: 'Playfair Display, serif',
      fontWeight: 700,
      textAlign: isMobile ? 'center' : 'left'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: isMobile ? '12px' : '15px',
      marginBottom: isMobile ? '12px' : '15px'
    },
    formGroup: {
      marginBottom: isMobile ? '12px' : '15px'
    },
    input: {
      width: '100%',
      padding: isMobile ? '12px' : '14px',
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(200, 169, 126, 0.2)',
      borderRadius: '12px',
      color: '#ffffff',
      fontSize: isMobile ? '14px' : '15px',
      transition: 'all 0.3s',
      outline: 'none',
      fontFamily: 'Poppins, sans-serif'
    },
    textarea: {
      width: '100%',
      padding: isMobile ? '12px' : '14px',
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(200, 169, 126, 0.2)',
      borderRadius: '12px',
      color: '#ffffff',
      fontSize: isMobile ? '14px' : '15px',
      transition: 'all 0.3s',
      outline: 'none',
      resize: 'vertical',
      minHeight: isMobile ? '80px' : '100px',
      fontFamily: 'Poppins, sans-serif'
    },
    submitBtn: {
      width: '100%',
      padding: isMobile ? '14px' : '16px',
      background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
      border: 'none',
      borderRadius: '12px',
      color: '#0A0F1C',
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.3s',
      marginTop: '15px',
      boxShadow: '0 10px 20px rgba(200, 169, 126, 0.3)'
    },
    successMessage: {
      textAlign: 'center',
      padding: isMobile ? '30px 20px' : '40px 30px',
      color: '#ffffff'
    },
    successIcon: {
      fontSize: isMobile ? '50px' : '60px',
      color: '#C8A97E',
      marginBottom: '15px'
    }
  };

  return (
    <section id="contact" style={styles.contact}>
      <div style={styles.container}>
        <motion.div 
          style={styles.sectionHeader}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span style={styles.subtitle}>Get In Touch</span>
          <h2 style={styles.h2}>
            Contact <span style={styles.titleGradient}>Us</span>
          </h2>
          <div style={styles.divider}></div>
        </motion.div>

        {/* Map Section */}
        <motion.div 
          style={styles.mapSection}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div style={styles.mapContainer}>
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=71.5%2C30.1%2C71.6%2C30.2&layer=mapnik&marker=30.1575%2C71.5249"
              style={styles.mapIframe}
              title="Clinic Location"
              allowFullScreen
              loading="lazy"
            ></iframe>
            
            <div style={styles.mapOverlay}>
              <motion.button
                style={styles.mapButton}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={openGoogleMaps}
              >
                <FaMapMarkerAlt /> View Map
              </motion.button>
              <motion.button
                style={{
                  ...styles.mapButton,
                  background: 'transparent',
                  color: '#C8A97E',
                  border: '1px solid #C8A97E'
                }}
                whileHover={{ scale: 1.05, background: 'rgba(200, 169, 126, 0.1)' }}
                whileTap={{ scale: 0.95 }}
                onClick={getDirections}
              >
                <FaDirections /> Directions
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div style={styles.content}>
          <motion.div 
            style={styles.infoCard}
            initial={{ opacity: 0, x: isMobile ? 0 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h3 style={styles.infoTitle}>Clinic Information</h3>
            
            {[
              { 
                icon: <FaMapMarkerAlt />, 
                label: 'Address', 
                value: 'Maxfax Dental Clinic, Alam Sher Center, Nishtar Rd, Multan',
                action: openGoogleMaps
              },
              { 
                icon: <FaPhone />, 
                label: 'Phone', 
                value: '0301 4484633',
                link: 'tel:03014484633'
              },
              { 
                icon: <FaClock />, 
                label: 'Hours', 
                value: 'Mon-Sat: 5PM-9PM\nSunday: Closed'
              },
              { 
                icon: <FaEnvelope />, 
                label: 'Email', 
                value: 'info@maxfaxdental.com',
                link: 'mailto:info@maxfaxdental.com'
              }
            ].map((item, index) => (
              <motion.div 
                key={index}
                style={styles.infoItem}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  scale: 1.02,
                  borderColor: '#C8A97E'
                }}
                onClick={item.action}
              >
                <div style={styles.infoIcon}>{item.icon}</div>
                <div style={styles.infoContent}>
                  <div style={styles.infoLabel}>{item.label}</div>
                  <div style={styles.infoValue}>
                    {item.link ? (
                      <a href={item.link} style={{ color: '#E5D5B5', textDecoration: 'none' }}>
                        {item.value}
                      </a>
                    ) : (
                      item.value
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            <div style={styles.socialLinks}>
              {[FaFacebookF, FaInstagram, FaTwitter, FaLinkedinIn].map((Icon, index) => (
                <motion.a
                  key={index}
                  href="#"
                  style={styles.socialLink}
                  whileHover={{ 
                    scale: 1.1,
                    background: 'linear-gradient(135deg, #C8A97E, #D4AF37)',
                    color: '#0A0F1C'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon />
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div 
            style={styles.formCard}
            initial={{ opacity: 0, x: isMobile ? 0 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {formStatus === 'success' ? (
              <motion.div 
                style={styles.successMessage}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
              >
                <FaCheckCircle style={styles.successIcon} />
                <h3 style={{ fontSize: '22px', marginBottom: '10px' }}>Thank You!</h3>
                <p style={{ color: '#A0A8B8' }}>
                  We'll get back to you soon.
                </p>
              </motion.div>
            ) : (
              <>
                <h3 style={styles.formTitle}>Send Message</h3>
                <form onSubmit={handleSubmit}>
                  <div style={styles.formRow}>
                    <input
                      type="text"
                      placeholder="First Name"
                      style={styles.input}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      style={styles.input}
                      required
                    />
                  </div>

                  <div style={styles.formRow}>
                    <input
                      type="email"
                      placeholder="Email"
                      style={styles.input}
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone"
                      style={styles.input}
                    />
                  </div>

                  <input
                    type="text"
                    placeholder="Subject"
                    style={styles.input}
                    required
                  />

                  <textarea
                    placeholder="Message"
                    style={styles.textarea}
                    required
                  ></textarea>

                  <motion.button
                    type="submit"
                    style={styles.submitBtn}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={formStatus === 'submitting'}
                  >
                    {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                  </motion.button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;